import { useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SearchableSelect,
  Separator,
  useToast,
} from "@racoon/ui";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useCreatePickupLocationGroupMutation } from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { TruckCapabilitiesModal } from "~/features/truck-capabilities/ui/truck-capabilities.modal";
import { AutoGenerateTimeField, DEFAULT_AUTO_GENERATE_TIME } from "./auto-generate-time.field";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerOrganizationId: number;
  trucks: ConnectProto.Truck[];
  onCreated?: (group: ConnectProto.PickupLocationGroup) => void;
};

export const CreateGroupModal = ({
  open,
  onOpenChange,
  ownerOrganizationId,
  trucks,
  onCreated,
}: Props) => {
  const t = useT();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [truckId, setTruckId] = useState<bigint | null>(null);
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(true);
  const [autoGenerateTime, setAutoGenerateTime] = useState(DEFAULT_AUTO_GENERATE_TIME);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [createGroup, { isLoading }] = useCreatePickupLocationGroupMutation();

  const selectedTruck = useMemo(
    () => trucks.find((truck) => truckId !== null && truck.id === truckId) ?? null,
    [trucks, truckId],
  );

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setName("");
      setTruckId(null);
      setAutoGenerateEnabled(true);
      setAutoGenerateTime(DEFAULT_AUTO_GENERATE_TIME);
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      const group = await createGroup({
        groupName: name.trim(),
        ownerOrganizationId: BigInt(ownerOrganizationId),
        ...(truckId !== null ? { defaultTruckId: truckId } : {}),
        ...(autoGenerateEnabled ? { autoGenerateTime } : {}),
      }).unwrap();
      toast({
        title: t(Namespace.PickupLocation, "groups.notifications.groupCreated"),
        description: group.name,
      });
      onCreated?.(group);
      handleClose(false);
    } catch {
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "groups.notifications.error"),
        description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!gap-0 !p-0 w-[600px] max-w-[600px] overflow-hidden rounded-2xl border-border-primary">
        <DialogHeader className="!px-4">
          <DialogTitle className="text-xl font-bold tracking-tight">
            {t(Namespace.PickupLocation, "groups.createGroup")}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-4">
            <Label
              htmlFor="create-group-name"
              className="w-[200px] shrink-0 text-sm font-medium tracking-[-0.2px] text-text-body"
            >
              {t(Namespace.PickupLocation, "groups.groupName")}
            </Label>
            <Input
              id="create-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(Namespace.PickupLocation, "groups.groupNamePlaceholder")}
              className="h-8 min-w-0 flex-1 rounded-lg border-border-primary px-2 text-sm text-text-body"
              autoFocus
            />
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-border-primary bg-slate-100 p-4">
            <div className="flex items-center gap-4">
              <Label className="w-[200px] shrink-0 text-sm font-medium tracking-[-0.2px] text-text-body">
                {t(Namespace.PickupLocation, "groups.responsibleTruck")}
              </Label>
              <div className="min-w-0 flex-1">
                <SearchableSelect
                  id="create-group-truck-search"
                  match="compact"
                  options={trucks.map((truck) => ({
                    id: String(truck.id),
                    name: truck.plateNumber ?? "",
                  }))}
                  value={truckId !== null ? String(truckId) : ""}
                  onChange={(id) => setTruckId(BigInt(id))}
                  placeholder={t(Namespace.PickupLocation, "groups.noTruck")}
                  searchPlaceholder={t(Namespace.PickupLocation, "groups.searchTruck")}
                  triggerClassName="h-8 w-full rounded-lg border-border-primary bg-white px-2"
                  contentClassName="w-64"
                />
              </div>
            </div>

            {selectedTruck?.truckType && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex flex-wrap gap-2 text-sm tracking-[-0.01px] text-icon-secondary">
                  <span>
                    {selectedTruck.truckType.manufacturer} {selectedTruck.truckType.model}
                  </span>
                  {!!selectedTruck.truckType.type && (
                    <span>
                      {t(
                        Namespace.Enum,
                        `TRUCK_TYPE_${ConnectProto.TruckType[selectedTruck.truckType.type]}` as TranslationKey,
                      )}
                    </span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 shrink-0 rounded-lg px-3 text-sm font-medium tracking-[-0.2px] text-text-information hover:text-text-information"
                  onClick={() => setCapabilitiesOpen(true)}
                >
                  {t(Namespace.PickupLocation, "groups.truckCapabilities")}
                </Button>
              </div>
            )}
          </div>

          <AutoGenerateTimeField
            id="create-group-auto-generate-time"
            enabled={autoGenerateEnabled}
            time={autoGenerateTime}
            onEnabledChange={setAutoGenerateEnabled}
            onTimeChange={setAutoGenerateTime}
          />
        </div>

        <Separator />

        <DialogFooter className="p-4">
          <Button
            className="h-8 rounded-lg bg-surface-action px-3 text-sm font-medium tracking-[-0.2px]"
            onClick={handleSubmit}
            disabled={!name.trim() || isLoading}
          >
            {t(Namespace.PickupLocation, "groups.create")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <TruckCapabilitiesModal
        open={capabilitiesOpen}
        onOpenChange={setCapabilitiesOpen}
        truck={selectedTruck}
      />
    </Dialog>
  );
};
